
import { useEffect } from 'react';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogUrl?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
  jsonLd?: object[];
}

export function SEO({
  title,
  description,
  keywords,
  canonical,
  ogTitle,
  ogDescription,
  ogImage,
  ogUrl,
  twitterTitle,
  twitterDescription,
  twitterImage,
  jsonLd
}: SEOProps) {
  useEffect(() => {
    // Update title
    if (title) {
      document.title = title;
    }

    // Update meta tags
    const updateMetaTag = (name: string, content: string, property = false) => {
      const selector = property ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      let meta = document.querySelector(selector) as HTMLMetaElement;
      if (!meta) {
        meta = document.createElement('meta');
        if (property) {
          meta.setAttribute('property', name);
        } else {
          meta.setAttribute('name', name);
        }
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    if (description) updateMetaTag('description', description);
    if (keywords) updateMetaTag('keywords', keywords);
    if (ogTitle) updateMetaTag('og:title', ogTitle, true);
    if (ogDescription) updateMetaTag('og:description', ogDescription, true);
    if (ogImage) updateMetaTag('og:image', ogImage, true);
    if (ogUrl) updateMetaTag('og:url', ogUrl, true);
    if (twitterTitle) updateMetaTag('twitter:title', twitterTitle);
    if (twitterDescription) updateMetaTag('twitter:description', twitterDescription);
    if (twitterImage) updateMetaTag('twitter:image', twitterImage);

    // Update canonical link
    if (canonical) {
      let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
      }
      link.setAttribute('href', canonical);
    }

    // Add JSON-LD scripts
    if (jsonLd && jsonLd.length > 0) {
      // Remove existing JSON-LD scripts added by this component
      const existingScripts = document.querySelectorAll('script[data-seo-component="true"]');
      existingScripts.forEach(script => script.remove());

      // Add new JSON-LD scripts
      jsonLd.forEach((data, index) => {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.setAttribute('data-seo-component', 'true');
        script.textContent = JSON.stringify(data);
        document.head.appendChild(script);
      });
    }
  }, [title, description, keywords, canonical, ogTitle, ogDescription, ogImage, ogUrl, twitterTitle, twitterDescription, twitterImage, jsonLd]);

  return null;
}

// Helper function to generate common JSON-LD schemas
export const generateOrganizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Castle & Castle Marketing Agency",
  "alternateName": "Castle & Castle",
  "description": "Full-stack growth marketing agency specializing in data-driven growth operations for fast-growing companies",
  "url": import.meta.env.VITE_SITE_URL || "https://example.com",
  "logo": "https://static.readdy.ai/image/c02e1238a9e0afa048eae5bb36a2fc1c/e8a8ca3e4b8833280e8b49a553477781.png",
  "contactPoint": [
    {
      "@type": "ContactPoint",
      "contactType": "customer service",
      "areaServed": ["US", "KE"],
      "availableLanguage": "English"
    }
  ],
  "address": [
    {
      "@type": "PostalAddress",
      "streetAddress": "Chester House, 2nd Floor",
      "addressLocality": "Nairobi",
      "addressCountry": "Kenya"
    },
    {
      "@type": "PostalAddress",
      "streetAddress": "111 W Monroe St, 12th Floor",
      "addressLocality": "Chicago",
      "addressRegion": "IL",
      "addressCountry": "USA"
    }
  ],
  "email": "info@castleandcastle.com",
  "sameAs": [
    "https://x.com/CastleCastleHQ"
  ],
  "serviceType": [
    "Marketing Agency",
    "Growth Marketing",
    "SEO Services",
    "PR Services",
    "Influencer Marketing",
    "Paid Advertising"
  ]
});

export const generateWebSiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "Castle & Castle Marketing Agency",
  "url": import.meta.env.VITE_SITE_URL || "https://example.com",
  "description": "Full-stack growth marketing agency specializing in data-driven growth operations for fast-growing companies",
  "publisher": {
    "@type": "Organization",
    "name": "Castle & Castle Marketing Agency"
  }
});

export const generateServiceSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Service",
  "name": "Growth Marketing Services",
  "description": "Comprehensive growth marketing services including SEO, PR, influencer marketing, and paid advertising",
  "provider": {
    "@type": "Organization",
    "name": "Castle & Castle Marketing Agency",
    "url": import.meta.env.VITE_SITE_URL || "https://example.com"
  },
  "serviceType": "Marketing Services",
  "areaServed": ["US", "KE"],
  "hasOfferCatalog": {
    "@type": "OfferCatalog",
    "name": "Marketing Services",
    "itemListElement": [
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "SEO Services",
          "description": "Search engine optimization services to improve online visibility"
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "PR Services",
          "description": "Public relations and media outreach services"
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Influencer Marketing",
          "description": "Strategic influencer partnerships and campaigns"
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Paid Advertising",
          "description": "Performance-driven paid advertising campaigns"
        }
      }
    ]
  }
});
